// Display a solid milled board, not the DXF's overlapping drafting surfaces.
// The visible +Z envelope becomes the front; sides and back share its boundary.
// Enclosed gaps with no source surface (including the back) remain through-holes.
import { stretchAxis } from "./millingStretch.js";
const cache = new WeakMap();

function throughHoles(coverage, width, height) {
  const core = new Uint8Array(coverage.length), holes = new Uint8Array(coverage.length);
  // Erosion separates genuine openings from single-sample CAD cracks.
  for (let y=1; y<height-1; y++) for (let x=1; x<width-1; x++) {
    const id=y*width+x;
    core[id]=!coverage[id] && !coverage[id-1] && !coverage[id+1] && !coverage[id-width] && !coverage[id+width];
  }
  const queue=new Int32Array(core.length);
  for(let id=0; id<core.length; id++) {
    if(!core[id]) continue;
    let head=0, tail=1, boundary=false;
    queue[0]=id; core[id]=0;
    while(head<tail) {
      const p=queue[head++], x=p%width, y=Math.floor(p/width);
      if(x<=1 || y<=1 || x>=width-2 || y>=height-2) boundary=true;
      for(const n of [p-1,p+1,p-width,p+width]) {
        if(n>=0 && n<core.length && core[n]) { core[n]=0; queue[tail++]=n; }
      }
    }
    if(boundary || tail<4) continue;
    // Undo erosion inside the gap only; do not remove measured frame surfaces.
    for(let i=0; i<tail; i++) {
      const p=queue[i];
      for(const n of [p,p-1,p+1,p-width,p+width]) if(!coverage[n]) holes[n]=1;
    }
  }
  return holes;
}

function closePerforatedGrid(positions, indices, nx, ny, solidCells) {
  const back=new Map();
  const backVertex=(id)=>{
    if(!back.has(id)) {
      back.set(id,positions.length/3);
      positions.push(positions[id*3],positions[id*3+1],0);
    }
    return back.get(id);
  };
  const wall=(a,b)=>{ const ba=backVertex(a), bb=backVertex(b); indices.push(a,ba,bb,a,bb,b); };
  for(let y=0;y<ny;y++) for(let x=0;x<nx;x++) {
    if(!solidCells[y*nx+x]) continue;
    const a=y*(nx+1)+x,b=a+1,c=a+nx+1,d=c+1;
    indices.push(a,b,d,a,d,c);
    const ba=backVertex(a),bb=backVertex(b),bc=backVertex(c),bd=backVertex(d);
    indices.push(ba,bd,bb,ba,bc,bd);
    if(y===0 || !solidCells[(y-1)*nx+x]) wall(a,b);
    if(x===nx-1 || !solidCells[y*nx+x+1]) wall(b,d);
    if(y===ny-1 || !solidCells[(y+1)*nx+x]) wall(d,c);
    if(x===0 || !solidCells[y*nx+x-1]) wall(c,a);
  }
}
export function fillMissingHeights(heights, width, height) {
  // Missing samples (especially at CAD boundary tolerances) must not become
  // full-thickness spikes. Extend the nearest measured surface instead.
  const repairBoundary=(indices)=>{
    const known=indices.map((id,i)=>Number.isFinite(heights[id])?i:-1).filter(i=>i>=0);
    if(!known.length) return;
    let right=0;
    indices.forEach((id,i)=>{
      if(Number.isFinite(heights[id])) return;
      while(right<known.length-1 && known[right]<i) right++;
      const b=known[right], a=known[Math.max(0,right-1)];
      const weight=a===b?0:Math.max(0,Math.min(1,(i-a)/(b-a)));
      heights[id]=heights[indices[a]]*(1-weight)+heights[indices[b]]*weight;
    });
  };
  // Repair along each edge first: taking an interior raised panel sample
  // would alter a rounded outside corner's original profile.
  repairBoundary(Array.from({length:width},(_,x)=>x));
  repairBoundary(Array.from({length:width},(_,x)=>(height-1)*width+x));
  repairBoundary(Array.from({length:height},(_,y)=>y*width));
  repairBoundary(Array.from({length:height},(_,y)=>y*width+width-1));
  const queue = new Int32Array(heights.length);
  let head=0, tail=0;
  for(let i=0;i<heights.length;i++) if(Number.isFinite(heights[i])) queue[tail++]=i;
  if(!tail) throw new Error("A DXF nem tartalmaz mintavételezhető elülső felületet.");
  const visit=(index,value)=>{
    if(!Number.isFinite(heights[index])) { heights[index]=value; queue[tail++]=index; }
  };
  while(head<tail) {
    const index=queue[head++], x=index%width, y=Math.floor(index/width), value=heights[index];
    if(x>0) visit(index-1,value);
    if(x+1<width) visit(index+1,value);
    if(y>0) visit(index-width,value);
    if(y+1<height) visit(index+width,value);
  }
}
export function closedMillingSolid(asset, resolution = 256, targetSize) {
  if (!targetSize && cache.has(asset) && resolution === 256) return cache.get(asset);
  const [sx, sy] = asset.size || [1, 1];
  let nx = Math.max(16, Math.round(resolution * sx / Math.max(sx, sy)));
  let ny = Math.max(16, Math.round(resolution * sy / Math.max(sx, sy)));
  const heights = new Float64Array((nx + 1) * (ny + 1)).fill(-Infinity);
  const coverage = new Uint8Array(heights.length);
  const p = asset.positions;
  for (let i = 0; i < asset.indices.length; i += 3) {
    const points = asset.indices.slice(i, i + 3).map((id) => [p[id*3]*nx, p[id*3+1]*ny, p[id*3+2]]);
    const [a, b, c] = points;
    // The board's back is not the front surface when a tiny CAD seam leaves
    // a sample uncovered. Otherwise it creates a false deep notch at the edge.
    // Imported A430I has a ~0.000057 mm tilt in the back plane. Use a
    // physical CAD tolerance, not an almost-exact normalized zero test.
    const backTolerance = Math.max(1e-6, 0.001 / (asset.size?.[2] || 1));
    const isBack = Math.max(a[2],b[2],c[2]) < backTolerance;
    const denominator = (b[1]-c[1])*(a[0]-c[0]) + (c[0]-b[0])*(a[1]-c[1]);
    if (Math.abs(denominator) < 1e-10) continue;
    const x0 = Math.max(0, Math.floor(Math.min(a[0], b[0], c[0])));
    const x1 = Math.min(nx, Math.ceil(Math.max(a[0], b[0], c[0])));
    const y0 = Math.max(0, Math.floor(Math.min(a[1], b[1], c[1])));
    const y1 = Math.min(ny, Math.ceil(Math.max(a[1], b[1], c[1])));
    for (let y=y0; y<=y1; y++) for (let x=x0; x<=x1; x++) {
      const u = ((b[1]-c[1])*(x-c[0]) + (c[0]-b[0])*(y-c[1]))/denominator;
      const v = ((c[1]-a[1])*(x-c[0]) + (a[0]-c[0])*(y-c[1]))/denominator;
      if (u < -1e-7 || v < -1e-7 || u+v > 1+1e-7) continue;
      const index = y*(nx+1)+x;
      coverage[index] = 1;
      if (!isBack) heights[index] = Math.max(heights[index], u*a[2]+v*b[2]+(1-u-v)*c[2]);
    }
  }
  const originalNx=nx, originalNy=ny;
  const holes=throughHoles(coverage,nx+1,ny+1);
  fillMissingHeights(heights,nx+1,ny+1);
  const positions = [], indices = [];
  let xs, ys, sampleHeight;
  if (targetSize) {
    xs = stretchAxis(sx, targetSize[0], nx, asset.stretchX, asset.repeatX);
    ys = stretchAxis(sy, targetSize[1], ny, asset.stretchY, asset.repeatY);
    if (xs.length * ys.length > 350000)
      throw new Error("Túl részletes az ismétlődő minta. Válassz hosszabb ismétlési mintaszakaszt.");
    sampleHeight = (x,y) => {
      const px=xs[x].source*originalNx, py=ys[y].source*originalNy;
      const ix=Math.min(originalNx-1,Math.floor(px)), iy=Math.min(originalNy-1,Math.floor(py));
      const tx=px-ix, ty=py-iy;
      const value=(xx,yy) => heights[yy*(originalNx+1)+xx];
      return (1-ty)*((1-tx)*value(ix,iy)+tx*value(ix+1,iy))+ty*((1-tx)*value(ix,iy+1)+tx*value(ix+1,iy+1));
    };
    nx=xs.length-1; ny=ys.length-1;
  }
  for (let y=0; y<=ny; y++) for (let x=0; x<=nx; x++) {
    const z = sampleHeight ? sampleHeight(x,y) : heights[y*(nx+1)+x];
    // Keep a backing even when the imported surface has missing patches.
    positions.push(xs ? xs[x].target : x/nx, ys ? ys[y].target : y/ny, Math.max(0.05, Math.min(1, z)));
  }
  if (holes.some(Boolean)) {
    const solidCells=new Uint8Array(nx*ny);
    for(let y=0;y<ny;y++) for(let x=0;x<nx;x++) {
      const px=xs ? (xs[x].source+xs[x+1].source)/2 : (x+.5)/nx;
      const py=ys ? (ys[y].source+ys[y+1].source)/2 : (y+.5)/ny;
      solidCells[y*nx+x]=holes[Math.round(py*originalNy)*(originalNx+1)+Math.round(px*originalNx)] ? 0 : 1;
    }
    closePerforatedGrid(positions,indices,nx,ny,solidCells);
  } else {
  for (let y=0; y<ny; y++) for (let x=0; x<nx; x++) {
    const a=y*(nx+1)+x, b=a+1, c=a+nx+1, d=c+1;
    indices.push(a,b,d, a,d,c);
  }
  const boundary = [];
  for (let x=0; x<nx; x++) boundary.push(x);
  for (let y=0; y<ny; y++) boundary.push(y*(nx+1)+nx);
  for (let x=nx; x>0; x--) boundary.push(ny*(nx+1)+x);
  for (let y=ny; y>0; y--) boundary.push(y*(nx+1));
  const back = boundary.map((id) => {
    const index=positions.length/3;
    positions.push(positions[id*3], positions[id*3+1], 0);
    return index;
  });
  const center=positions.length/3;
  positions.push(0.5,0.5,0);
  boundary.forEach((a, i) => {
    const j=(i+1)%boundary.length, b=boundary[j];
    indices.push(a,back[i],back[j], a,back[j],b, center,back[j],back[i]);
  });
  }
  const result = { positions, indices, size: asset.size };
  if (!targetSize && resolution === 256) cache.set(asset, result);
  return result;
}
